import React from 'react';
import { Layers, Cpu, Cloud, Lightbulb, Box, Brain, Database, Network } from 'lucide-react';

export const Services = () => {
  const services = [
    {
      icon: Layers,
      title: 'Immersive Experiences',
      description: 'We design interactive digital environments that enhance engagement, learning, and operational readiness.',
      features: [
        'Virtual and interactive digital experiences',
        '3D environments and simulations',
        'Training, education, and awareness journeys',
        'Cultural, tourism, and experiential solutions'
      ],
      accentColor: 'from-[#5B42FF]/20 to-transparent'
    },
    {
      icon: Cpu,
      title: 'Advanced Digital Solutions',
      description: 'We build intelligent, flexible solutions that improve efficiency and decision-making.',
      features: [
        'Digital platforms and applications',
        'Intelligent automation solutions',
        'Data-driven systems and analytics'
      ],
      accentColor: 'from-[#5B42FF]/20 to-transparent'
    },
    {
      icon: Cloud,
      title: 'Digital & Cloud Services',
      description: 'We provide reliable, secure, and scalable digital services tailored to organizational needs.',
      features: [
        'Digital product design and development',
        'Managed services',
        'Cloud services and infrastructure support',
        'Systems integration'
      ],
      accentColor: 'from-[#5B42FF]/20 to-transparent'
    },
    {
      icon: Lightbulb,
      title: 'Innovation & Strategy',
      description: 'We help organizations plan, design, and execute sustainable digital transformation initiatives.',
      features: [
        'Digital strategies and transformation roadmaps',
        'Innovation hub design and implementation',
        'Emerging technology advisory',
        'Experience and service design'
      ],
      accentColor: 'from-[#5B42FF]/20 to-transparent'
    }
  ];

  return (
    <section id="services" className="py-16 md:py-32 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/3 left-0 w-64 md:w-96 h-64 md:h-96 bg-[#5B42FF]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/3 right-0 w-64 md:w-96 h-64 md:h-96 bg-[#5B42FF]/5 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 relative">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-xs md:text-sm font-semibold text-[#5B42FF] uppercase tracking-wider mb-3 md:mb-4">Products & Services</h2>
          <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6">
            Future-Ready Digital Solutions
          </h3>
          <p className="text-base md:text-xl text-white/70 max-w-3xl mx-auto px-4">
            Our offerings address real-world challenges through thoughtfully designed digital solutions and future-ready platforms.
          </p>
          <div className="w-20 md:w-24 h-1 bg-[#5B42FF] mx-auto mt-4 md:mt-6"></div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8 mt-12 md:mt-16">
          {services.map((service, index) => (
            <div
              key={index}
              className="group relative p-6 md:p-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl md:rounded-2xl hover:border-[#5B42FF]/50 transition-all duration-500 hover:transform hover:scale-[1.02] overflow-hidden"
            >
              {/* Gradient Accent */}
              <div className={`absolute top-0 right-0 w-48 md:w-64 h-48 md:h-64 bg-gradient-to-br ${service.accentColor} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-full blur-2xl`}></div>
              
              <div className="relative">
                {/* Icon */}
                <div className="w-14 h-14 md:w-16 md:h-16 bg-[#5B42FF]/20 rounded-xl flex items-center justify-center mb-4 md:mb-6 group-hover:bg-[#5B42FF]/30 transition-all duration-300 group-hover:scale-110">
                  <service.icon className="w-7 h-7 md:w-8 md:h-8 text-[#5B42FF]" />
                </div>

                {/* Title & Description */}
                <h4 className="text-xl md:text-2xl font-bold text-white mb-3 md:mb-4">{service.title}</h4>
                <p className="text-sm md:text-base text-white/70 mb-4 md:mb-6 leading-relaxed">{service.description}</p>

                {/* Features List */}
                <ul className="space-y-2 md:space-y-3">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start space-x-2 md:space-x-3 group/item">
                      <div className="mt-1.5 w-1.5 h-1.5 bg-[#5B42FF] rounded-full flex-shrink-0 group-hover/item:scale-150 transition-transform duration-300"></div>
                      <span className="text-sm md:text-base text-white/80 group-hover/item:text-white transition-colors duration-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};